import React, { useState, useEffect, useRef } from 'react';
import { csrfFetch } from '../../utils/csrfFetch';

/**
 * ProjectSelector - Dropdown for selecting and managing projects (SAT-63)
 *
 * Features:
 * - Shows all projects with color indicators
 * - "All Projects" option to view all conversations
 * - Create new project option
 * - Quick access to project settings
 */
function ProjectSelector({ selectedProject, onSelectProject, onCreateProject, onOpenSettings }) {
  const [isOpen, setIsOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const dropdownRef = useRef(null);
  const createInputRef = useRef(null);

  // Fetch projects from API
  const fetchProjects = async () => {
    try {
      const response = await csrfFetch('/api/projects', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCreateInput(false);
        setNewProjectName('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus input when showing create form
  useEffect(() => {
    if (showCreateInput && createInputRef.current) {
      createInputRef.current.focus();
    }
  }, [showCreateInput]);

  const handleSelect = (project) => {
    onSelectProject(project);
    setIsOpen(false);
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;

    setIsCreating(true);
    try {
      const response = await csrfFetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newProjectName.trim(),
          color: getRandomColor()
        })
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(prev => [...prev, data.project]);
        onSelectProject(data.project);
        setNewProjectName('');
        setShowCreateInput(false);
        setIsOpen(false);
        if (onCreateProject) onCreateProject(data.project);
      }
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCreateProject();
    } else if (e.key === 'Escape') {
      setShowCreateInput(false);
      setNewProjectName('');
    }
  };

  // Generate a random project color
  const getRandomColor = () => {
    const colors = [
      '#005d7e', // Decisiv brand
      '#e89213', // Decisiv rust
      '#3B82F6', // Blue
      '#10B981', // Green
      '#8B5CF6', // Purple
      '#F59E0B', // Amber
      '#EF4444', // Red
      '#EC4899', // Pink
      '#06B6D4', // Cyan
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const selectedName = selectedProject ? selectedProject.name : 'All Projects';
  const selectedColor = selectedProject?.color || '#6B7280';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2
          bg-white dark:bg-gray-800
          border border-gray-200 dark:border-gray-700
          rounded-lg hover:bg-gray-50 dark:hover:bg-gray-750
          transition-colors text-sm"
      >
        <div className="flex items-center gap-2 min-w-0">
          {/* Color indicator */}
          <div
            className="w-3 h-3 rounded-full shrink-0"
            style={{ backgroundColor: selectedColor }}
          />
          <span className="truncate text-gray-700 dark:text-gray-200 font-medium">
            {selectedName}
          </span>
          {selectedProject && (
            <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
              ({selectedProject.conversation_count || 0})
            </span>
          )}
        </div>

        {/* Chevron */}
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50
          bg-white dark:bg-gray-800
          border border-gray-200 dark:border-gray-700
          rounded-lg shadow-lg overflow-hidden"
        >
          <div className="max-h-64 overflow-y-auto">
            {/* All Projects option */}
            <button
              onClick={() => handleSelect(null)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left
                hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors
                ${!selectedProject ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
            >
              <div className="w-3 h-3 rounded-full bg-gray-400 shrink-0" />
              <span className="text-sm text-gray-700 dark:text-gray-200">All Projects</span>
            </button>

            {/* Divider */}
            {projects.length > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
            )}

            {/* Loading state */}
            {isLoading && (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                Loading projects...
              </div>
            )}

            {/* Projects list */}
            {!isLoading && projects.map((project) => (
              <div
                key={project.id}
                className={`flex items-center justify-between group
                  hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors
                  ${selectedProject?.id === project.id ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
              >
                <button
                  onClick={() => handleSelect(project)}
                  className="flex-1 flex items-center gap-2 px-3 py-2 text-left"
                >
                  <div
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: project.color || '#005d7e' }}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200 truncate">
                    {project.name}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
                    ({project.conversation_count || 0})
                  </span>
                </button>

                {/* Settings icon */}
                {onOpenSettings && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenSettings(project);
                      setIsOpen(false);
                    }}
                    className="p-2 opacity-0 group-hover:opacity-100 transition-opacity
                      text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Project settings"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                )}
              </div>
            ))}

            {/* No projects message */}
            {!isLoading && projects.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                No projects yet
              </div>
            )}
          </div>

          {/* Create new project */}
          <div className="border-t border-gray-200 dark:border-gray-700">
            {showCreateInput ? (
              <div className="p-2">
                <div className="flex items-center gap-2">
                  <input
                    ref={createInputRef}
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Project name"
                    className="flex-1 px-2 py-1.5 text-sm
                      bg-gray-100 dark:bg-gray-700
                      border border-gray-200 dark:border-gray-600
                      rounded focus:outline-hidden focus:ring-2 focus:ring-decisiv-blue
                      text-gray-700 dark:text-gray-200"
                    disabled={isCreating}
                  />
                  <button
                    onClick={handleCreateProject}
                    disabled={!newProjectName.trim() || isCreating}
                    className="px-2 py-1.5 text-sm font-medium
                      bg-decisiv-blue text-white rounded
                      hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed
                      transition-colors"
                  >
                    {isCreating ? '...' : 'Add'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowCreateInput(true)}
                className="w-full flex items-center gap-2 px-3 py-2.5
                  text-sm text-decisiv-blue hover:bg-gray-50 dark:hover:bg-gray-750
                  transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Create new project</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectSelector;
